import type { User } from "firebase/auth";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as api from "./api";
import { darker, normalRing, pathsOf, polygonOf, ringOf, sameRing, shapeProblem, starterRing, versionText, versionWhen } from "./areaMap";
import { type GoogleMap, type Maps, pointOf, type Polygon } from "./maps";
import { isReloading, useHoldReload } from "./offline/hold";
import { saveFailure } from "./save";
import { Skeleton } from "./Skeleton";

export const USER_SHAPE_TEXT = "Changed by a person — the night job keeps it";

/** Rule V-17: move, add and remove the corners of one area, then save the shape. */
export function AreaShapeEditor({
  maps,
  map,
  user,
  area,
  onSaved,
  onClose,
  historyHost,
  mapBox,
  timeZone,
}: {
  maps: Maps;
  map: RefObject<GoogleMap | null>;
  user: User;
  area: api.Area;
  onSaved: (area: api.Area, text: string) => void;
  onClose: () => void;
  /** The place beside the map for the shape history; null: under the editor. */
  historyHost?: HTMLElement | null;
  mapBox?: RefObject<HTMLElement | null>;
  timeZone?: string;
}) {
  const start = useMemo(() => {
    const ring = ringOf(area.polygon);
    if (ring.length) return normalRing(ring);
    const view = map.current?.getBounds();
    return starterRing(view ? pointOf(view.getSouthWest()) : null, view ? pointOf(view.getNorthEast()) : null);
  }, [area, map]);
  const shape = useRef<Polygon | null>(null);
  const [ring, setRing] = useState(start);
  const [corner, setCorner] = useState<number | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const resetButton = useRef<HTMLButtonElement>(null);
  const dirty = !area.polygon || !sameRing(ring, start);
  const [versions, setVersions] = useState<api.AreaVersion[] | "failed" | undefined>(undefined);
  const [preview, setPreview] = useState<string | null>(null);
  const [reverting, setReverting] = useState<api.AreaVersion | null>(null);

  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.areaVersions(token, area.id))
      .then(
        (found) => live && setVersions(found.versions),
        () => live && setVersions("failed"),
      );
    return () => {
      live = false;
    };
  }, [user, area.id]);

  const shown = Array.isArray(versions) ? versions.find((version) => version.id === preview) : undefined;
  useEffect(() => {
    const view = map.current;
    const paths = pathsOf(shown?.polygon ?? null);
    if (!view || !paths.length) return;
    const made = new maps.maps.Polygon({
      map: view,
      paths,
      clickable: false,
      strokeColor: "#ffffff",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: darker(area.colour, 0.3),
      fillOpacity: 0.15,
      zIndex: 2,
    });
    return () => made.setMap(null);
  }, [maps, map, area.colour, shown]);

  useEffect(() => {
    const view = map.current;
    if (!view) return;
    const made = new maps.maps.Polygon({
      map: view,
      paths: start,
      editable: true,
      draggable: false,
      strokeColor: darker(area.colour),
      strokeOpacity: 1,
      strokeWeight: 3,
      fillColor: area.colour,
      fillOpacity: 0.25,
      zIndex: 1,
    });
    shape.current = made;
    const path = made.getPath();
    const read = () => {
      setRing(normalRing(path.getArray().map(pointOf)));
      setCorner(null);
      setProblem("");
    };
    const remove = (vertex: number | undefined) => {
      if (vertex !== undefined && path.getArray().length > 3) path.removeAt(vertex);
    };
    const listeners = [
      path.addListener("insert_at", read),
      path.addListener("remove_at", read),
      path.addListener("set_at", read),
      made.addListener("click", (event) => setCorner(event.vertex ?? null)),
      made.addListener("rightclick", (event) => remove(event.vertex)),
    ];
    return () => {
      for (const listener of listeners) listener.remove();
      made.setMap(null);
      shape.current = null;
    };
  }, [maps, map, area, start]);

  useHoldReload(!sameRing(ring, start), `the shape of area ${area.code}`);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => !isReloading() && e.preventDefault();
    addEventListener("beforeunload", warn);
    return () => removeEventListener("beforeunload", warn);
  }, [dirty]);

  const removeCorner = () => {
    if (corner === null || ring.length <= 3) return;
    shape.current?.getPath().removeAt(corner);
  };
  const send = async (call: (token: string) => Promise<api.Area>, text: string) => {
    setBusy(true);
    setProblem("");
    try {
      onSaved(await call(await user.getIdToken()), text);
    } catch (e) {
      setProblem(saveFailure(e, "The shape").text);
      setBusy(false);
    }
  };
  const save = () => {
    const found = shapeProblem(ring);
    if (found) {
      setProblem(found);
      return;
    }
    void send((token) => api.saveAreaPolygon(token, area.id, polygonOf(ring)), `Saved the shape of area ${area.code}. The night job keeps it.`);
  };
  const reset = () => void send((token) => api.resetAreaPolygon(token, area.id), `Area ${area.code} has the proposed shape again.`);
  const revert = (version: api.AreaVersion) =>
    void send(
      (token) => api.revertAreaPolygon(token, area.id, version.id),
      `Area ${area.code} has the shape of ${versionWhen(version, timeZone)} again. The night job keeps it.`,
    );

  const history = (
    <section className="area-shape-history" data-testid="area-shape-history" aria-labelledby="area-shape-history-title">
      <h3 id="area-shape-history-title">Shape history</h3>
      {versions === undefined && <Skeleton rows={2} label="Loading the shape history" />}
      {versions === "failed" && (
        <p className="error" role="alert" data-testid="area-shape-history-failed">
          The shape history did not load. Close the editor and open it again.
        </p>
      )}
      {Array.isArray(versions) && !versions.length && <p className="sub">No shape of this area is kept yet. Each saved shape is kept here.</p>}
      {Array.isArray(versions) && versions.length > 0 && (
        <>
          <p className="sub">Point at a version, or tap it, to see its shape on the map in white.</p>
          <ol className="area-shape-versions">
            {versions.map((version) => (
              <li
                key={version.id}
                data-testid="area-shape-version"
                data-source={version.source}
                data-current={version.current || undefined}
                className={preview === version.id ? "picked" : undefined}
                onMouseEnter={() => setPreview(version.id)}
                onMouseLeave={() => setPreview((current) => (current === version.id ? null : current))}
              >
                <button
                  type="button"
                  className="link"
                  aria-pressed={preview === version.id}
                  data-testid="area-shape-version-show"
                  onClick={() => {
                    if (preview !== version.id) showMap(mapBox?.current);
                    setPreview(preview === version.id ? null : version.id);
                  }}
                >
                  {versionText(version, timeZone)}
                </button>
                {version.note && <span className="sub"> · {version.note}</span>}
                {version.current ? (
                  <span className="sub" data-testid="area-shape-version-current">
                    {" "}
                    · The shape now
                  </span>
                ) : (
                  version.polygon && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="quiet"
                        data-testid="area-shape-revert"
                        data-version={version.id}
                        disabled={busy || reverting !== null}
                        onClick={() => {
                          setReverting(version);
                          setPreview(version.id);
                          showMap(mapBox?.current);
                        }}
                      >
                        Revert to this
                      </button>
                    </>
                  )
                )}
                {reverting?.id === version.id && (
                  <p className="confirm" data-testid="area-shape-revert-ask">
                    Give area {area.code} this shape again? {dirty && area.polygon ? "Your changes that are not saved go. " : ""}It is then a shape
                    that a person drew: the night job does not change it.{" "}
                    <button type="button" className="danger" data-testid="area-shape-revert-confirm" disabled={busy} onClick={() => revert(version)}>
                      Revert the shape
                    </button>{" "}
                    <button
                      type="button"
                      data-testid="area-shape-revert-keep"
                      autoFocus
                      onClick={() => {
                        setReverting(null);
                        requestAnimationFrame(() => focusRevert(document, version.id));
                      }}
                    >
                      Cancel
                    </button>
                  </p>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );

  return (
    <div className="area-shape-edit" data-testid="area-shape-edit" data-area={area.code}>
      <div className="pin-map-bar">
        <button type="button" className="primary" data-testid="area-shape-save" disabled={busy || !dirty} onClick={save}>
          {busy ? "Saving…" : "Save shape"}
        </button>
        <button type="button" data-testid="area-shape-cancel" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        {corner !== null && (
          <button type="button" data-testid="area-shape-remove-corner" disabled={busy || ring.length <= 3} onClick={removeCorner}>
            Remove this corner
          </button>
        )}
        {area.polygon_source === "user" && (
          <button type="button" ref={resetButton} className="quiet" data-testid="area-shape-reset" disabled={busy || asking} onClick={() => setAsking(true)}>
            Reset to the proposed shape
          </button>
        )}
      </div>
      {asking && (
        <p className="confirm" data-testid="area-shape-reset-ask">
          Give area {area.code} the proposed shape again? The night job then changes it with the pins.{" "}
          <button type="button" className="danger" data-testid="area-shape-reset-confirm" disabled={busy} onClick={reset}>
            Reset the shape
          </button>{" "}
          <button
            type="button"
            data-testid="area-shape-reset-keep"
            autoFocus
            onClick={() => {
              setAsking(false);
              requestAnimationFrame(() => resetButton.current?.focus());
            }}
          >
            Cancel
          </button>
        </p>
      )}
      <p className="sub" role="status" data-testid="area-shape-status">
        Area {area.code} · {ring.length} corners{area.polygon_source === "user" && ` · ${USER_SHAPE_TEXT}`}
        {!area.polygon && " · This area had no shape. Drag the corners to where it is."}
      </p>
      <p className="sub">Drag a corner to move it. Drag the point in the middle of an edge to add a corner. Tap a corner, then remove it.</p>
      {problem && (
        <p role="alert" className="error" data-testid="area-shape-problem">
          {problem}
        </p>
      )}
      {historyHost ? createPortal(history, historyHost) : history}
    </div>
  );
}

/** After Cancel on the revert question, the focus goes back to the "Revert to this" button that opened it. */
export function focusRevert(doc: Pick<Document, "querySelector">, versionId: string): boolean {
  const button = doc.querySelector<HTMLButtonElement>(`[data-testid="area-shape-revert"][data-version="${versionId}"]`);
  button?.focus();
  return Boolean(button);
}

/** Scroll the map into view when it is not on the screen, so a picked version shows. */
export function showMap(box: HTMLElement | null | undefined) {
  if (!box) return;
  const at = box.getBoundingClientRect();
  if (at.top >= 0 && at.top < innerHeight / 2) return;
  console.info("shape history: scroll the map into view");
  box.scrollIntoView({ block: "start", behavior: "smooth" });
}
